import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdPreview from './AdPreview';
import { getApiBaseUrl } from '../config/apiBase';
import './ReportPage.css';

const API_BASE_URL = getApiBaseUrl();

// Helper function to format numbers with commas
function formatNumber(num) {
  if (num === null || num === undefined) return '0';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Helper function to calculate date ranges
function getDateRange(period) {
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today
  
  let start = new Date();
  
  switch (period) {
    case 'today':
      start = new Date(today);
      start.setHours(0, 0, 0, 0);
      break;
    case 'last7days':
      start = new Date(today);
      start.setDate(start.getDate() - 6); // Last 7 days including today
      start.setHours(0, 0, 0, 0);
      break;
    case 'last30days':
      start = new Date(today);
      start.setDate(start.getDate() - 29); // Last 30 days including today
      start.setHours(0, 0, 0, 0);
      break;
    case 'thisMonth':
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      start.setHours(0, 0, 0, 0);
      break;
    case 'lastMonth':
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      end.setHours(23, 59, 59, 999);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0]
      };
    default:
      start = new Date(today);
      start.setDate(start.getDate() - 29);
      start.setHours(0, 0, 0, 0);
  }
  
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: today.toISOString().split('T')[0]
  };
}

function ReportPage() {
  const [searchParams] = useSearchParams();
  const [adId, setAdId] = useState('');
  const [secret, setSecret] = useState('');
  const [ad, setAd] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('last30days');
  const [sortColumn, setSortColumn] = useState(null); // 'country', 'city', 'views'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [countryFilter, setCountryFilter] = useState('all'); // 'all' or specific country
   const [reportKey, setReportKey] = useState(searchParams.get('key') || null);
  const [prefilled, setPrefilled] = useState(false);
  const [businessSearchQuery, setBusinessSearchQuery] = useState('');
  const [businessSearchResults, setBusinessSearchResults] = useState([]);
  const [businessSearchLoading, setBusinessSearchLoading] = useState(false);
  const [businessSearchError, setBusinessSearchError] = useState(null);

  const fetchReportData = async (finalAdId, finalSecret, selectedPeriod, updateAd = true) => {
    setLoading(true);
    setError(null);
    
    // Only clear ad and stats if this is a new report (not just a date range change)
    if (updateAd) {
      setAd(null);
      setStats(null);
    }

    const dateRangeObj = getDateRange(selectedPeriod);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ad-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          adId: finalAdId, 
          secret: finalSecret,
          startDate: dateRangeObj.startDate,
          endDate: dateRangeObj.endDate,
          key: reportKey
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load report');
      }

      const data = await response.json();
      if (updateAd) {
        setAd({ id: finalAdId, ...data.ad });
      }
      setStats(data.stats);
      // Reset sorting when new data is loaded
      setSortColumn(null);
      setSortDirection('asc');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e, prefilledAdId = null, prefilledSecret = null, period = null) => {
    if (e) {
      e.preventDefault();
    }

    const finalAdId = prefilledAdId || adId.trim();
    const finalSecret = prefilledSecret || secret.trim();
    const selectedPeriod = period || dateRange;
    
    await fetchReportData(finalAdId, finalSecret, selectedPeriod, true);
  };

  const handleDateRangeChange = async (period) => {
    if (!ad || !ad.id) return; // Don't update if no ad is loaded
    
    setDateRange(period);
    await fetchReportData(ad.id, secret, period, false); // Don't update ad, only stats
  };

  // Prefill form from URL parameters and auto-submit
  useEffect(() => {
    const urlAdId = searchParams.get('adId');
    const urlSecret = searchParams.get('secret');
    const urlKey = searchParams.get('key');
    
    if (urlAdId) {
      setAdId(urlAdId);
    }
    if (urlSecret) {
      setSecret(urlSecret);
    }
    if (urlKey) {
      setReportKey(urlKey);
    }
    if (urlAdId && urlSecret) {
      setPrefilled(true);
    }
    
    // Auto-submit if both parameters are present
    if (urlAdId && urlSecret) {
      // Small delay to ensure state is set
      const timer = setTimeout(() => {
        handleSubmit(null, urlAdId, urlSecret);
      }, 100);
      
      return () => clearTimeout(timer);
    } else if (urlKey === 'A1b2C3') {
      // Admin aggregate mode: load report without requiring adId/secret
      const timer = setTimeout(() => {
        fetchReportData(null, null, dateRange, true);
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleBusinessSearch = async (e) => {
    if (e) e.preventDefault();
    if (!businessSearchQuery.trim()) {
      setBusinessSearchError('יש להזין שם עסק לחיפוש');
      setBusinessSearchResults([]);
      return;
    }

    setBusinessSearchLoading(true);
    setBusinessSearchError(null);
    setBusinessSearchResults([]);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/ad-report/search?businessName=${encodeURIComponent(businessSearchQuery.trim())}`
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'חיפוש נכשל');
      }

      const data = await response.json();
      const results = Array.isArray(data.results) ? data.results : [];
      if (results.length === 0) {
        setBusinessSearchError('לא נמצאו פרסומות בשם הזה');
      }
      setBusinessSearchResults(results);
    } catch (err) {
      setBusinessSearchError(err.message || 'חיפוש נכשל');
    } finally {
      setBusinessSearchLoading(false);
    }
  };

  const handleUseAdId = (selectedAdId) => {
    setAdId(selectedAdId);
    setBusinessSearchResults([]);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortData = (data) => {
    if (!sortColumn || !Array.isArray(data)) return data;

    const sorted = [...data].sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case 'country':
          aVal = (a.country || '').toLowerCase();
          bVal = (b.country || '').toLowerCase();
          break;
        case 'city':
          aVal = (a.city || '').toLowerCase();
          bVal = (b.city || '').toLowerCase();
          break;
        case 'views':
          aVal = a.views || 0;
          bVal = b.views || 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const renderAnalytics = () => {
    if (!stats?.analytics) return null;
    const { available, totalViews, viewsByLocation, reason, normalizedViewsByLocation, normalizedTotalViews } = stats.analytics;

    if (!available) {
      return (
        <div className="report-card">
          <h3>כמות הצגות של הפרסומת לפי עיר</h3>
          <p className="muted">Unavailable: {reason || 'No data'}</p>
        </div>
      );
    }

    const baseData = normalizedViewsByLocation || viewsByLocation || [];
    // Drop \"unknown\" locations from the display
    const dataForDisplay = Array.isArray(baseData)
      ? baseData.filter(
          (row) =>
            row &&
            String(row.country || '').toLowerCase() !== 'unknown' &&
            String(row.city || '').toLowerCase() !== 'unknown'
        )
      : [];

    // Build list of countries for filter dropdown
    const countryOptions = Array.isArray(dataForDisplay)
      ? Array.from(
          new Set(
            dataForDisplay
              .map((row) => row.country)
              .filter((c) => c && typeof c === 'string')
          )
        ).sort((a, b) => a.localeCompare(b))
      : [];

    const filteredData =
      countryFilter === 'all'
        ? dataForDisplay
        : dataForDisplay.filter((row) => row.country === countryFilter);

    const totalForDisplay =
      Array.isArray(filteredData) && filteredData.length > 0
        ? filteredData.reduce((sum, row) => sum + (row.views || 0), 0)
        : normalizedTotalViews ?? totalViews ?? 0;

    const sortedData = sortData(filteredData);

    // Aggregate by country (after filters and after dropping unknowns)
    const viewsByCountryMap = new Map();
    if (Array.isArray(filteredData)) {
      filteredData.forEach((row) => {
        const country = row.country || 'אחר';
        const current = viewsByCountryMap.get(country) || 0;
        viewsByCountryMap.set(country, current + (row.views || 0));
      });
    }
    const viewsByCountry = Array.from(viewsByCountryMap.entries())
      .map(([country, views]) => ({ country, views }))
      .sort((a, b) => (b.views || 0) - (a.views || 0));

    return (
      <div className="report-card">
        <h3>
          כמות הצגות של הפרסומת לפי עיר
          {countryOptions.length > 0 && (
            <select
              id="countryFilter"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              style={{ marginInlineStart: '8px', fontSize: '0.85em' }}
            >
              <option value="all">כל המדינות</option>
              {countryOptions.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          )}
        </h3>
        <div className="metric">{formatNumber(totalForDisplay)} total</div>
        {Array.isArray(sortedData) && sortedData.length > 0 ? (
          <>
            <table className="report-table">
              <thead>
                <tr>
                  <th
                    className={`sortable ${sortColumn === 'country' ? `sort-${sortDirection}` : ''}`}
                    onClick={() => handleSort('country')}
                  >
                    מדינה
                    {sortColumn === 'country' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th
                    className={`sortable ${sortColumn === 'city' ? `sort-${sortDirection}` : ''}`}
                    onClick={() => handleSort('city')}
                  >
                    עיר
                    {sortColumn === 'city' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                  <th
                    className={`sortable ${sortColumn === 'views' ? `sort-${sortDirection}` : ''}`}
                    onClick={() => handleSort('views')}
                  >
                    צפיות
                    {sortColumn === 'views' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, idx) => (
                  <tr key={`${row.country}-${row.city}-${idx}`}>
                    <td>{row.country}</td>
                    <td>{row.city}</td>
                    <td>{formatNumber(row.views)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {viewsByCountry.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <h4>כמות הצגת פרסומות לפי מדינה</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>מדינה</th>
                      <th>צפיות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewsByCountry.map((row) => (
                      <tr key={row.country}>
                        <td>{row.country}</td>
                        <td>{formatNumber(row.views)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <p className="muted">No location breakdown found.</p>
        )}
      </div>
    );
  };

  return (
    <div className="report-page">
      <div className="report-hero">
        <h1>דוח פרסומים בפלטפורמת GSRadio TV</h1>
        {!prefilled && <p>Enter your ad ID and secret to view performance.</p>}
      </div>

      {!prefilled && !reportKey && (
        <form className="report-form" onSubmit={handleSubmit}>
          <div className="business-search">
            <div className="business-search-header">
              <label htmlFor="businessName">חיפוש לפי שם העסק</label>
              <button type="button" onClick={handleBusinessSearch} disabled={businessSearchLoading}>
                {businessSearchLoading ? 'מחפש...' : 'חפש'}
              </button>
            </div>
            <input
              id="businessName"
              value={businessSearchQuery}
              onChange={(e) => setBusinessSearchQuery(e.target.value)}
              placeholder="לדוגמה: שופרסל, דיזינגוף סנטר"
            />
            {businessSearchError && <div className="error">{businessSearchError}</div>}
            {businessSearchResults.length > 0 && (
              <div className="business-search-results">
                {businessSearchResults.map((result) => (
                  <div key={result.adId} className="business-search-result">
                    <div>
                      <div className="business-name">{result.businessName}</div>
                      <div className="muted">{result.description || '—'}</div>
                    </div>
                    <div className="result-actions">
                      <span className="muted">ID: {result.adId}</span>
                      <button type="button" onClick={() => handleUseAdId(result.adId)} disabled={loading}>
                        השתמש במזהה
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="form-row">
            <label htmlFor="adId">Ad ID</label>
            <input
              id="adId"
              value={adId}
              onChange={(e) => setAdId(e.target.value)}
              placeholder="e.g., miluim-1"
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="secret">Secret</label>
            <input
              id="secret"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="Enter your secret"
              required
            />
          </div>
          <button type="submit" disabled={loading}>
            {loading ? 'Loading...' : 'View Report'}
          </button>
          {error && <div className="error">{error}</div>}
        </form>
      )}

      {ad && (
        <div className="report-content">
          {!ad.isAggregate && (
            <div
              className={`report-preview ${
                ad.show_fullscreen_video === true ||
                (ad.vast_tag_url && String(ad.vast_tag_url).trim())
                  ? 'has-fullscreen-video'
                  : ''
              }`}
            >
              <h2>תצוגה מקדימה של הפרסומת</h2>
              <AdPreview ad={ad} />
            </div>
          )}

          <div className="report-time-selector">
            <button
              className={dateRange === 'today' ? 'active' : ''}
              onClick={() => handleDateRangeChange('today')}
              disabled={loading}
            >
              היום
            </button>
            <button
              className={dateRange === 'last7days' ? 'active' : ''}
              onClick={() => handleDateRangeChange('last7days')}
              disabled={loading}
            >
              7 ימים אחרונים
            </button>
            <button
              className={dateRange === 'last30days' ? 'active' : ''}
              onClick={() => handleDateRangeChange('last30days')}
              disabled={loading}
            >
              30 ימים אחרונים
            </button>
            <button
              className={dateRange === 'thisMonth' ? 'active' : ''}
              onClick={() => handleDateRangeChange('thisMonth')}
              disabled={loading}
            >
              החודש
            </button>
            <button
              className={dateRange === 'lastMonth' ? 'active' : ''}
              onClick={() => handleDateRangeChange('lastMonth')}
              disabled={loading}
            >
              חודש קודם
            </button>
          </div>

          <div className="report-stats">
            {renderAnalytics()}
          </div>
        </div>
      )}

      {loading && (
        <div className="report-loading-overlay">
          <div className="report-spinner"></div>
          <p>טוען דוח...</p>
        </div>
      )}
    </div>
  );
}

export default ReportPage;
